import type { FileData } from '../../../../../components';

export enum STUDY_MODE {
  FULL_TIME = 'FULL_TIME',
  PART_TIME = 'PART_TIME',
}

export enum DEGREE_LEVEL {
  BACHELOR_DEGREE = 'BACHELOR_DEGREE',
  BACHELOR_DEGREE_MINOR = 'BACHELOR_DEGREE_MINOR',
  MASTER_DEGREE = 'MASTER_DEGREE',
  DOCTORATE_DEGREE = 'DOCTORATE_DEGREE',
  OTHERS = 'OTHERS',
}

export enum QUALIFICATION_MEANS {
  COURSEWORK = 'COURSEWORK',
  COURSEWORK_RESEARCH = 'COURSEWORK_RESEARCH',
  RESEARCH = 'RESEARCH',
}

export type AcademicQualificationInstitution = {
  name: string;
  country: string;
};

export type MarkInput = {
  course: string;
  credit: number;
  result: string;
};

export type Mark = {
  course: string;
  credit: number | null;
  result: string;
};

export type MarkConversion = {
  convertedMark: string;
  digitToLetterRule: string;
  letterToGPARule: string;
};

export type GPAConvertRule = {
  _id?: string;
  ruleName: string;
  maxGPARange?: number;
  minGPARange?: number;
  note?: string;
  content?: string;
};

export type AcademicQualification = {
  presentIndicator: boolean;
  degreeLevel: string;
  programmeName: string;
  from: string | null;
  to: string | null;
  studyMode: string; // keyof STUDY_MODE;
  currentStudyYear?: number | '';
  institution: {
    country: string;
    name: string;
  };
  instructionMedium: string;
  otherInstructionMedium: string;
  studyField: string;
  qualificationMeans: string; // keyof QUALIFICATION_MEANS;
  qualificationStatus: string; // keyof QUALIFICATION_STATUS;
  awardDate: string | null;
  awardClassification: string; // keyof AWARD_CLASSIFICATION;
  officialMark: string | null;
  outOfGpa: number | string | null;
  marks: Mark[];
  markConversion?: MarkConversion;
  convertGPARuleId?: string;
  letterToGPARule: string;
  digitToLetterRule: string;
  convertedMark: string;
  expectUpdateDate: string | null;
  withdrawDate: string | null;
  academicOnlineVerificationCode?: string;
  degreeOnlineVerificationCode?: string;
  officialClassRanking: number | null;
  outOfRank: number | null;
  certificates: FileData[];
  transcripts: FileData[];
  markExplanations: FileData[];
  classRankProofs: FileData[];
  otherDocuments: FileData[];
  gpaIndicator: boolean;
  rankIndicator: boolean;
  academicCredentialIndicator: boolean;
  degreeCertIndicator: boolean;
};

export type AcademicQualificationFormData = {
  academicQualificationId?: string;
  presentIndicator: boolean;
  degreeLevel: string;
  programmeName: string;
  from: string | null;
  to: string | null;
  studyMode: string; // keyof STUDY_MODE;
  currentStudyYear?: number | '';
  institutionName: string;
  institutionCountry: string;
  instructionMedium: string;
  otherInstructionMedium: string;
  studyField: string;
  qualificationMeans: string; // keyof QUALIFICATION_MEANS;
  qualificationStatus: string; // keyof QUALIFICATION_STATUS;
  awardDate: string | null;
  awardClassification: string; // keyof AWARD_CLASSIFICATION;
  officialMark: string | null;
  outOfGpa: number | string | null;
  marks: Mark[];
  markConversion?: MarkConversion;
  convertGPARuleId?: string;
  letterToGPARule: string;
  digitToLetterRule: string;
  convertedMark: string;
  expectUpdateDate: string | null;
  withdrawDate: string | null;
  academicOnlineVerificationCode: string;
  degreeOnlineVerificationCode: string;
  officialClassRanking: number | null;
  outOfRank: number | null;
  certificates: FileData[];
  transcripts: FileData[];
  markExplanations: FileData[];
  classRankProofs: FileData[];
  otherDocuments: FileData[];
  gpaIndicator: boolean;
  rankIndicator: boolean;
  academicCredentialIndicator: boolean;
  degreeCertIndicator: boolean;
};

export type SupportingDocuments = Pick<
  AcademicQualificationFormData,
  | 'certificates'
  | 'transcripts'
  | 'markExplanations'
  | 'classRankProofs'
  | 'otherDocuments'
>;
